import {
  AbilityClass,
  ConditionsMatcher,
  PureAbility,
  subject as subjectHelper,
} from '@casl/ability';

import {
  Ability,
  AbilityConditions,
  Actions,
  ConsoleAbility,
  ConsoleRules,
  Subjects,
} from './types';

const ConsoleAbilityClass = PureAbility as AbilityClass<ConsoleAbility>;

type Conditions = any;

const conditionsMatcher = {
  subjectId: (condition: Conditions, context: AbilityConditions) => {
    return condition.subjectId === context.subjectId;
  },
  organizationId: (condition: Conditions, context: AbilityConditions) => {
    return condition.organizationId === context.organizationId;
  },
  enterpriseId: (condition: Conditions, context: AbilityConditions) => {
    return condition.enterpriseId === context.enterpriseId;
  },
  clusterId: (condition: Conditions, context: AbilityConditions) => {
    return condition.clusterId === context.clusterId;
  },
  default: (conditions: Conditions) => (entity: any) => {
    return Object.keys(conditions).every(
      (key) => entity[key] === conditions[key]
    );
  },
};

const conditionsMatcherFn: ConditionsMatcher<Conditions> = (ruleConditions) => {
  if (ruleConditions && Object.keys(ruleConditions).length > 0) {
    return (context) => {
      // if rule has no subjectId, delegate to default field-equality matcher
      if (!ruleConditions.subjectId) {
        return conditionsMatcher.default(ruleConditions)(context);
      }

      // every rule has subjectId which is userId or groupId
      const matchSubject = conditionsMatcher.subjectId(ruleConditions, context);

      // if rule has clusterId, it means that rules scope is cluster
      if (ruleConditions?.clusterId) {
        const matchCluster = conditionsMatcher.clusterId(
          ruleConditions,
          context
        );
        return matchSubject && matchCluster;
      }

      // match child organizations
      if (ruleConditions?.organizationId && ruleConditions?.enterpriseId) {
        const matchOrganization = conditionsMatcher.organizationId(
          ruleConditions,
          context
        );
        const matchEnterprise = conditionsMatcher.enterpriseId(
          ruleConditions,
          context
        );

        return matchSubject && matchOrganization && matchEnterprise;
      }

      // match default organizations
      if (ruleConditions?.organizationId) {
        const matchOrganization = conditionsMatcher.organizationId(
          ruleConditions,
          context
        );

        return matchSubject && matchOrganization;
      }

      if (ruleConditions.enterpriseId) {
        const matchEnterprise = conditionsMatcher.enterpriseId(
          ruleConditions,
          context
        );

        return matchSubject && matchEnterprise;
      }

      return conditionsMatcher.subjectId(ruleConditions, context);
    };
  }

  return conditionsMatcher.default(ruleConditions ?? {});
};

export const defineAbilityFor = (
  rules: ConsoleRules[],
  context: AbilityConditions
): Omit<Ability, 'isLoading'> => {
  const ability = new ConsoleAbilityClass(rules, {
    conditionsMatcher: conditionsMatcherFn,
  });

  const can = (
    action: Actions,
    subject: Subjects,
    conditions: AbilityConditions = {}
  ) => {
    const { subjectId, organizationId, enterpriseId, clusterId } = conditions;

    return ability.can(
      action,
      subjectHelper<Subjects, any>(subject, {
        subjectId: subjectId ?? context.subjectId,
        organizationId: organizationId ?? context.organizationId,
        enterpriseId: enterpriseId ?? context.enterpriseId,
        clusterId: clusterId ?? context.clusterId,
      })
    );
  };

  const canMany = (
    rules: Array<[Actions, Subjects]>,
    conditions: AbilityConditions = {}
  ) => {
    return rules.map(([action, subject]) => can(action, subject, conditions));
  };

  const cannot = (
    action: Actions,
    subject: Subjects,
    conditions: AbilityConditions = {}
  ) => {
    const { subjectId, organizationId, enterpriseId, clusterId } = conditions;
    return ability.cannot(
      action,
      subjectHelper<Subjects, any>(subject, {
        subjectId: subjectId ?? context.subjectId,
        organizationId: organizationId ?? context.organizationId,
        enterpriseId: enterpriseId ?? context.enterpriseId,
        clusterId: clusterId ?? context.clusterId,
      })
    );
  };

  const cannotMany = (
    rules: Array<[Actions, Subjects]>,
    conditions: AbilityConditions = {}
  ) => {
    return rules.map(([action, subject]) =>
      cannot(action, subject, conditions)
    );
  };

  const canOneOf = (
    action: Actions,
    subjects: Subjects[],
    conditions: AbilityConditions = {}
  ) => {
    return subjects.some((subject) => can(action, subject, conditions));
  };

  const cannotOneOf = (
    action: Actions,
    subjects: Subjects[],
    conditions: AbilityConditions = {}
  ) => {
    return subjects.some((subject) => cannot(action, subject, conditions));
  };

  const canAllOf = (
    action: Actions,
    subjects: Subjects[],
    conditions: AbilityConditions = {}
  ) => {
    return subjects.every((subject) => can(action, subject, conditions));
  };

  const cannotAllOf = (
    action: Actions,
    subjects: Subjects[],
    conditions: AbilityConditions = {}
  ) => {
    return subjects.every((subject) => cannot(action, subject, conditions));
  };

  const relevantRuleFor = (
    action: Actions,
    subject: Subjects,
    conditions: AbilityConditions = {}
  ) => {
    const { subjectId, organizationId, enterpriseId, clusterId } = conditions;
    return ability.relevantRuleFor(
      action,
      subjectHelper<Subjects, any>(subject, {
        subjectId: subjectId ?? context.subjectId,
        organizationId: organizationId ?? context.organizationId,
        enterpriseId: enterpriseId ?? context.enterpriseId,
        clusterId: clusterId ?? context.clusterId,
      })
    );
  };

  return {
    can,
    canMany,
    cannot,
    cannotMany,
    canOneOf,
    canAllOf,
    cannotOneOf,
    cannotAllOf,
    relevantRuleFor,
  };
};
